// Thumbnail grid rendered inside the edit/detail modal of each daily-report
// view. Automatic private URLs come from the component-scoped Query owner; a
// click still mints a fresh URL so an open modal never hands out an expired one.
//
// Read-only in v1: no add/remove. The daily-photos bucket grants authenticated
// INSERT (mig 099) and authenticated SELECT (mig 031); admin add/remove is a
// future build that would need an UPDATE/DELETE policy plus a delete path.

import React from 'react';

import {useAuth} from '../contexts/AuthContext.jsx';
import {imageAltText} from '../lib/imageAlt.js';
import {sb} from '../lib/supabase.js';
import InlineNotice from './InlineNotice.jsx';
import type {Notice} from './InlineNotice.jsx';
import {dailyPhotoUrlFor, normalizeDailyPhotoUrlInput, useDailyPhotoUrls} from './dailyPhotoUrlData.js';

// Only the two members this grid reads. The full metadata shape written by
// src/lib/dailyPhotos.ts is {path, name, mime, size_bytes, captured_at}; the
// column is jsonb, so the three unread members are deliberately not restated
// here as if they were guaranteed.
export type DailyPhotoRef = {
  path?: string | null;
  name?: string | null;
};

// `photos` is optional because half the call sites read it off a possibly-absent
// form object (`form?.photos`); the Array.isArray below is what makes that safe.
type DailyPhotoThumbnailsProps = {
  photos?: DailyPhotoRef[] | null;
};

export default function DailyPhotoThumbnails({photos}: DailyPhotoThumbnailsProps) {
  const list = Array.isArray(photos) ? photos : [];
  const {authState, rolePreview} = useAuth();
  // Automatic thumbnail signing is a scoped Query concern. This component is
  // the presenter and keeps only the user-initiated fresh click-open action.
  const urls = useDailyPhotoUrls({sb, authState, rolePreview, photos: list});
  // Click-open failure is surfaced inline beneath the thumbnail grid so the
  // host modal does not need to thread a notice setter through; it clears for
  // each new attempt.
  const [openNotice, setOpenNotice] = React.useState<Notice | null>(null);

  async function openFull(path: string) {
    setOpenNotice(null);
    // Re-fetch the signed URL on click: the modal may have been open longer
    // than the original 10-minute expiry window.
    const {data, error} = await sb.storage.from('daily-photos').createSignedUrl(path, 600);
    if (error) {
      setOpenNotice({kind: 'error', message: 'Cannot open photo: ' + error.message});
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  }

  if (list.length === 0) return null;

  return (
    <div data-photo-thumbnails="1" style={{marginTop: 8}}>
      <div
        style={{fontSize: 11, fontWeight: 700, color: 'var(--ink-muted)', marginBottom: 6, textTransform: 'uppercase'}}
      >
        Photos ({list.length})
      </div>
      <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
        {list.map((p, i) => {
          const photo = normalizeDailyPhotoUrlInput(p);
          if (!p || !photo) return null;
          // Bound once so the click handler closes over a value TypeScript has
          // already proved is a string: a property narrowing does not survive
          // into a nested arrow function, only a local binding does.
          const photoPath = photo.path;
          const {url, error} = dailyPhotoUrlFor(urls, photo);
          return (
            <button
              key={photoPath + i}
              data-photo-thumb={photoPath}
              type="button"
              onClick={() => openFull(photoPath)}
              title={p.name || photoPath}
              style={{
                width: 80,
                height: 80,
                border: '1px solid var(--border-strong)',
                borderRadius: 10,
                overflow: 'hidden',
                padding: 0,
                background: 'var(--divider)',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              {url ? (
                <img
                  src={url}
                  alt={imageAltText(p.name, {fallback: 'Daily report photo', index: i, total: list.length})}
                  style={{width: '100%', height: '100%', objectFit: 'cover'}}
                />
              ) : error ? (
                <span style={{fontSize: 10, color: '#b91c1c'}}>?</span>
              ) : (
                <span style={{fontSize: 10, color: 'var(--ink-faint)'}}>&#8230;</span>
              )}
            </button>
          );
        })}
      </div>
      {openNotice && (
        <div style={{marginTop: 8}}>
          <InlineNotice notice={openNotice} onDismiss={() => setOpenNotice(null)} />
        </div>
      )}
    </div>
  );
}
